/**
 * 轻量全局提示 Hook。
 *
 * 基于 MUI Snackbar，提供 show(message, severity) 与需要在组件中渲染的 Toast 节点。
 * 用法：const { show, Toast } = useToast(); ... return (<><Toast/>...</>);
 *
 * @module hooks/useToast
 */
import { useCallback, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { AlertColor } from '@mui/material/Alert';

/** useToast 返回值。 */
interface UseToastResult {
  /** 触发提示。 */
  show: (message: string, severity?: AlertColor) => void;
  /** 需要渲染在组件树中的提示节点（可能为 null）。 */
  Toast: JSX.Element | null;
}

/**
 * 全局提示 Hook。
 *
 * @returns show 方法与 Toast 节点。
 */
export function useToast(): UseToastResult {
  const [message, setMessage] = useState<string | null>(null);
  const [severity, setSeverity] = useState<AlertColor>('success');

  const show = useCallback((msg: string, sev: AlertColor = 'success'): void => {
    setSeverity(sev);
    setMessage(msg);
  }, []);

  const Toast: JSX.Element | null = message ? (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={3000}
      onClose={() => setMessage(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={() => setMessage(null)}
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  ) : null;

  return { show, Toast };
}
